// Reusable API layer — one place for base URL, timeout, retry, cancellation and
// failure toasts. Replaces raw `fetch()` scattered across utils/components.
//
// `apiFetch` returns the raw Response (drop-in for `fetch`) so existing callers
// keep their `.ok` / `.json()` handling, while gaining: an AbortController
// timeout, a caller-cancellation signal, retry-with-backoff on network/5xx
// errors (never on 4xx), and an optional failure toast. `apiJson` is a thin
// convenience that parses JSON and throws a typed error on non-2xx.

// Single source of truth for the backend origins. Both are baked in at build
// time by Vite, so a local stack must be built with VITE_PR_API_URL /
// VITE_WEBAUTH_PUBLIC_URL pointing at its own hosts (see .env.example).
const trimSlash = (u) => u.replace(/\/$/, '')

export const API_BASE = trimSlash(
  import.meta.env.VITE_PR_API_URL || 'https://api.projet-resurgence.fr'
)

export const WEBAUTH_PUBLIC_URL = trimSlash(
  import.meta.env.VITE_WEBAUTH_PUBLIC_URL || 'https://auth.projet-resurgence.fr'
)

// Le tableau de bord : la carte n'y appelle rien, mais elle y renvoie — une
// conversation ouverte depuis la carte se lit là-bas. Baked at build time comme
// les deux autres ; en coder l'URL en dur casserait la pile locale, où le
// dashboard est sur `play.pr.localhost` (règle 10 du CLAUDE.md racine).
export const DASHBOARD_PUBLIC_URL = trimSlash(
  import.meta.env.VITE_DASHBOARD_PUBLIC_URL || 'https://play.projet-resurgence.fr'
)

// Toast sink — registered once by App (utils are non-React and cannot call the
// React showToast directly). No-op until registered.
let toastHandler = null
export const registerToastHandler = (fn) => { toastHandler = fn }
const emitToast = (msg, type = 'error') => { if (toastHandler) toastHandler(msg, type) }

export class ApiError extends Error {
  constructor(message, { status = 0, cause } = {}) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.cause = cause
  }
}

const sleep = (ms) => new Promise((r) => setTimeout(r, ms))

// Opt-in in-memory GET cache (S14). Keyed by URL; stores a cloned Response so the
// body can be re-read on each hit. Pass `cacheTtl` (ms) to enable per call.
const responseCache = new Map() // url -> { at, resp }
export const clearApiCache = () => responseCache.clear()

// Merge a caller AbortSignal with an internal timeout controller so either can
// abort the request.
const withTimeout = (timeout, signal) => {
  const ctrl = new AbortController()
  const onAbort = () => ctrl.abort(signal?.reason)
  if (signal) {
    if (signal.aborted) ctrl.abort(signal.reason)
    else signal.addEventListener('abort', onAbort, { once: true })
  }
  const timer = timeout ? setTimeout(() => ctrl.abort(new DOMException('timeout', 'TimeoutError')), timeout) : null
  const cleanup = () => {
    if (timer) clearTimeout(timer)
    if (signal) signal.removeEventListener('abort', onAbort)
  }
  return { signal: ctrl.signal, cleanup }
}

/**
 * @param {string} path  Absolute URL or path appended to API_BASE.
 * @param {object} opts  fetch options plus:
 *   retries (default 1), timeout ms (default 15000), signal (caller cancel),
 *   toastOnError (default false), toastMessage (override), credentials
 *   (default 'include' so the SSO cookie is sent).
 * @returns {Promise<Response>}
 */
export async function apiFetch(path, opts = {}) {
  const {
    timeout = 15000,
    signal,
    toastOnError = false,
    toastMessage,
    cacheTtl = 0,
    // Default to same-origin (the native fetch default) — public API GETs
    // return `Access-Control-Allow-Origin: *`, which the browser rejects if
    // credentials are sent. Private endpoints opt in with credentials:'include'.
    credentials = 'same-origin',
    ...init
  } = opts
  // Never auto-retry mutations — a retried POST could double-execute (e.g. build
  // a structure twice). GETs are safe to retry on transient failures.
  const isMutation = /^(POST|PUT|PATCH|DELETE)$/i.test(init.method || 'GET')
  const retries = opts.retries ?? (isMutation ? 0 : 1)
  const url = /^https?:\/\//.test(path) ? path : `${API_BASE}${path.startsWith('/') ? '' : '/'}${path}`

  // Cache lookup (GET only).
  if (cacheTtl > 0 && !isMutation) {
    const hit = responseCache.get(url)
    if (hit && Date.now() - hit.at < cacheTtl) return hit.resp.clone()
  }

  let lastErr
  for (let attempt = 0; attempt <= retries; attempt++) {
    const { signal: mergedSignal, cleanup } = withTimeout(timeout, signal)
    try {
      const resp = await fetch(url, { ...init, credentials, signal: mergedSignal })
      cleanup()
      // Retry server errors (transient); return 4xx to the caller as-is.
      if (resp.status >= 500 && attempt < retries) {
        lastErr = new ApiError(`HTTP ${resp.status}`, { status: resp.status })
        await sleep(300 * (attempt + 1))
        continue
      }
      if (cacheTtl > 0 && !isMutation && resp.ok) {
        responseCache.set(url, { at: Date.now(), resp: resp.clone() })
      }
      return resp
    } catch (err) {
      cleanup()
      // Caller-initiated cancel — never retry, never toast, propagate.
      if (signal?.aborted) throw err
      lastErr = err
      if (attempt < retries) { await sleep(300 * (attempt + 1)); continue }
    }
  }
  if (toastOnError) emitToast(toastMessage || 'Erreur réseau — réessayez plus tard.')
  throw new ApiError('network', { cause: lastErr })
}

// Parse JSON, throwing ApiError on non-2xx. Use when the caller just wants data.
export async function apiJson(path, opts = {}) {
  const resp = await apiFetch(path, opts)
  if (!resp.ok) throw new ApiError(`HTTP ${resp.status}`, { status: resp.status })
  return resp.json()
}
